import React, { useState, useEffect } from "react";
import Button from '../../components/ui/Button';

import Select from '../../components/ui/Select';
import MetricCard from './components/MetricCard';
import AlertBanner from './components/AlertBanner';
import ThreatMonitor from './components/ThreatMonitor';
import RangerDeployment from './components/RangerDeployment';
import VisitorMetrics from './components/VisitorMetrics';
import BiodiversityHealth from './components/BiodiversityHealth';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import { Activity, TrendingUp, Shield, AlertTriangle, Clock } from "lucide-react";

export default function CommandCenterDashboard() {
  const [timeRange, setTimeRange] = useState('today');
  const [alerts, setAlerts] = useState([
  { id: 1, message: "⚠️ Park temporarily closed due to Typhoon Odette - Expected reopening December 15", severity: "high", time: "2 hours ago" },
  { id: 2, message: "🌊 High tide warning for underground river tours - Limited slots available", severity: "medium", time: "5 hours ago" },
  { id: 3, message: "🦅 Rare Philippine Eagle sighting reported near Trail B - Viewing opportunities available", severity: "low", time: "8 hours ago" }]
  );
  const [scrollingAlert, setScrollingAlert] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setScrollingAlert((prev) => (prev + 1) % alerts?.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const timeRangeOptions = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' }];


  const metrics = [
  {
    title: 'Active Visitors',
    value: '247',
    unit: 'people',
    trend: 'up',
    trendValue: '+12%',
    icon: 'Users',
    iconColor: 'bg-info/20 text-info',
    status: 'normal'
  },
  {
    title: 'Biodiversity Health',
    value: '78',
    unit: '%',
    trend: 'up',
    trendValue: '+3%',
    icon: 'Activity',
    iconColor: 'bg-success/20 text-success',
    status: 'success'
  },
  {
    title: 'Active Threats',
    value: '3',
    unit: 'incidents',
    trend: 'down',
    trendValue: '-25%',
    icon: 'AlertTriangle',
    iconColor: 'bg-error/20 text-error',
    status: 'critical'
  },
  {
    title: 'Rangers on Patrol',
    value: '18',
    unit: 'active',
    trend: 'up',
    trendValue: '+2',
    icon: 'Shield',
    iconColor: 'bg-secondary/20 text-secondary',
    status: 'normal'
  }];


  const threats = [
  {
    id: 1,
    type: 'Illegal Logging',
    severity: 'critical',
    location: 'Zone A-3, Northern Sector',
    description: 'Unauthorized tree cutting detected by acoustic sensors. Estimated 3-5 individuals involved.',
    detectedAt: '12:45 PM',
    assignedTo: 'Ranger Team Alpha',
    status: 'active'
  },
  {
    id: 2,
    type: 'Wildlife Poaching',
    severity: 'high',
    location: 'Zone B-7, Eastern Boundary',
    description: 'Suspicious activity near endangered species habitat. Camera traps triggered.',
    detectedAt: '11:20 AM',
    assignedTo: 'Ranger Team Bravo',
    status: 'monitoring'
  },
  {
    id: 3,
    type: 'Unauthorized Access',
    severity: 'medium',
    location: 'Zone C-2, Restricted Area',
    description: 'Visitors detected in closed conservation zone. No immediate threat to wildlife.',
    detectedAt: '10:15 AM',
    assignedTo: 'Ranger Team Charlie',
    status: 'monitoring'
  }];


  const rangers = [
  {
    id: 1,
    name: 'Carlos Mendoza',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffe872cd-1763294679798.png",
    avatarAlt: 'Professional Filipino ranger Carlos Mendoza in uniform with short black hair and confident expression',
    zone: 'Northern Sector',
    currentLocation: 'Zone A-3 Patrol Route',
    status: 'responding'
  },
  {
    id: 2,
    name: 'Maria Santos',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11cc0d1f9-1764781647308.png",
    avatarAlt: 'Filipino ranger Maria Santos in conservation uniform with tied back hair and warm smile',
    zone: 'Eastern Boundary',
    currentLocation: 'Zone B-7 Observation Post',
    status: 'on-patrol'
  },
  {
    id: 3,
    name: 'Juan Reyes',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b97f5076-1765151405000.png",
    avatarAlt: 'Senior Filipino ranger Juan Reyes in field uniform with weathered face and determined look',
    zone: 'Western Corridor',
    currentLocation: 'Zone D-1 Base Station',
    status: 'on-patrol'
  },
  {
    id: 4,
    name: 'Elena Cruz',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14b0a2f83-1764830567861.png",
    avatarAlt: 'Filipino ranger Elena Cruz in conservation gear with ponytail and professional demeanor',
    zone: 'Southern Region',
    currentLocation: 'Zone E-5 Wildlife Monitoring',
    status: 'on-patrol'
  }];


  const visitorData = [
  { name: 'Mon', visitors: 145, permits: 132 },
  { name: 'Tue', visitors: 178, permits: 165 },
  { name: 'Wed', visitors: 203, permits: 189 },
  { name: 'Thu', visitors: 167, permits: 154 },
  { name: 'Fri', visitors: 234, permits: 218 },
  { name: 'Sat', visitors: 289, permits: 267 },
  { name: 'Sun', visitors: 247, permits: 231 }];


  const species = [
  {
    id: 1,
    name: 'Philippine Eagle',
    scientificName: 'Pithecophaga jefferyi',
    population: '24 individuals',
    status: 'monitoring',
    lastSurvey: '3 days ago',
    trend: 'Stable'
  },
  {
    id: 2,
    name: 'Tamaraw',
    scientificName: 'Bubalus mindorensis',
    population: '156 individuals',
    status: 'healthy',
    lastSurvey: '1 week ago',
    trend: 'Increasing'
  },
  {
    id: 3,
    name: 'Philippine Cockatoo',
    scientificName: 'Cacatua haematuropygia',
    population: '8 individuals',
    status: 'critical',
    lastSurvey: '2 days ago',
    trend: 'Declining'
  }];


  const quickActions = [
  { id: 'emergency', label: 'Emergency Response', icon: 'Siren', variant: 'destructive' },
  { id: 'deploy', label: 'Deploy Rangers', icon: 'Shield', variant: 'default' },
  { id: 'alert', label: 'Send Alert', icon: 'Bell', variant: 'outline' },
  { id: 'report', label: 'Generate Report', icon: 'FileText', variant: 'outline' },
  { id: 'weather', label: 'Weather Update', icon: 'Cloud', variant: 'outline' },
  { id: 'communication', label: 'Stakeholder Comm', icon: 'MessageSquare', variant: 'outline' }];


  const recentActivities = [
  {
    id: 1,
    type: 'threat',
    title: 'Threat Detected',
    description: 'Illegal logging activity in Zone A-3',
    timestamp: '2 minutes ago',
    user: 'System Alert'
  },
  {
    id: 2,
    type: 'patrol',
    title: 'Patrol Completed',
    description: 'Team Alpha completed northern sector patrol',
    timestamp: '45 minutes ago',
    user: 'Carlos Mendoza'
  },
  {
    id: 3,
    type: 'visitor',
    title: 'Visitor Permit Issued',
    description: '15 new permits approved for weekend tours',
    timestamp: '1 hour ago',
    user: 'Admin Office'
  },
  {
    id: 4,
    type: 'research',
    title: 'Research Data Updated',
    description: 'Philippine Eagle population survey completed',
    timestamp: '3 hours ago',
    user: 'Dr. Ana Lopez'
  }];


  const handleDismissAlert = (id) => {
    setAlerts(alerts?.filter((alert) => alert?.id !== id));
  };

  const handleAlertAction = (id) => {
    console.log('Alert action triggered:', id);
  };

  const handleQuickAction = (actionId) => {
    console.log('Quick action triggered:', actionId);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 transition-colors">
      {/* Scrolling Alerts Banner */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 dark:from-red-800 dark:to-orange-800 text-white py-3 px-4 shadow-lg overflow-hidden">
        <div className="max-w-7xl mx-auto flex items-center">
          <AlertTriangle className="h-5 w-5 mr-3 flex-shrink-0 animate-pulse" />
          <div className="flex-1 overflow-hidden">
            <div className="animate-scroll-left whitespace-nowrap">
              <span className="inline-block px-8 text-sm font-semibold">
                {alerts?.[scrollingAlert]?.message}
              </span>
              <span className="inline-block px-8 text-xs opacity-75">
                {alerts?.[scrollingAlert]?.time}
              </span>
            </div>
          </div>
          <Clock className="h-4 w-4 ml-3 flex-shrink-0" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Alerts Section */}
        {alerts?.length > 0 &&
        <div className="mb-8">
            {alerts?.map((alert) =>
          <AlertBanner
            key={alert?.id}
            severity={alert?.severity}
            message={alert?.message}
            timestamp={alert?.timestamp}
            actionLabel={alert?.actionLabel}
            onAction={() => handleAlertAction(alert?.id)}
            onDismiss={() => handleDismissAlert(alert?.id)} />
          )}
          </div>
        }

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold text-foreground mb-2">Command Center Dashboard</h2>
            <p className="text-muted-foreground">Real-time conservation monitoring and operational control</p>
          </div>
          <div className="flex items-center gap-4">
            <Select
              options={timeRangeOptions}
              value={timeRange}
              onChange={setTimeRange}
              className="w-40" />

            <Button variant="default" iconName="Download" iconPosition="left">
              Export Report
            </Button>
          </div>
        </div>

        {/* Stats Grid - Add new Enforcement Index card */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* ... keep existing MetricCard components ... */}
          
          {/* NEW: PRECISION Enforcement Index */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border-l-4 border-purple-500 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
              PRECISION Enforcement Index
            </h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              94.2%
            </p>
            <div className="flex items-center text-sm text-green-600 dark:text-green-400">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>+5.3% from last month</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Patrol coverage & response efficiency
            </p>
          </div>
        </div>

        {/* Main Dashboard Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Threat Monitor - Spans 2 columns */}
          <div className="lg:col-span-2">
            <ThreatMonitor threats={threats} />
          </div>

          {/* Ranger Deployment */}
          <div>
            <RangerDeployment rangers={rangers} />
          </div>
        </div>

        {/* Visitor Metrics and Biodiversity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <VisitorMetrics data={visitorData} title="Weekly Visitor Trends" />
          <BiodiversityHealth species={species} />
        </div>

        {/* Quick Actions and Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <QuickActions actions={quickActions} onActionClick={handleQuickAction} />
          </div>
          <div className="lg:col-span-2">
            <RecentActivity activities={recentActivities} />
          </div>
        </div>

        {/* NEW: Featured Articles Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Activity className="h-6 w-6 mr-2 text-emerald-600 dark:text-emerald-400" />
              Featured Conservation Articles
            </h2>
            <button className="text-emerald-600 dark:text-emerald-400 hover:underline text-sm font-semibold">
              View All
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group cursor-pointer">
              <div className="relative h-48 rounded-lg overflow-hidden mb-3">
                <img
                  src=""
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />

                <div className="absolute top-3 left-3 bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Conservation
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-emerald-600 transition-colors">
                New Water Quality Monitoring System Deployed
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Advanced sensors now track underground river ecosystem health in real-time...
              </p>
              <span className="text-xs text-gray-500 dark:text-gray-500">December 8, 2025</span>
            </div>

            <div className="group cursor-pointer">
              <div className="relative h-48 rounded-lg overflow-hidden mb-3">
                <img
                  src=""
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />

                <div className="absolute top-3 left-3 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Biodiversity
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-emerald-600 transition-colors">
                Record 15 New Species Documented This Year
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Research teams celebrate major biodiversity discovery milestone...
              </p>
              <span className="text-xs text-gray-500 dark:text-gray-500">December 5, 2025</span>
            </div>

            <div className="group cursor-pointer">
              <div className="relative h-48 rounded-lg overflow-hidden mb-3">
                <img
                  src=""
                  alt=""
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />

                <div className="absolute top-3 left-3 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Community
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-emerald-600 transition-colors">
                Local Youth Rangers Program Expansion
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                50 new youth volunteers trained in wildlife monitoring and conservation...
              </p>
              <span className="text-xs text-gray-500 dark:text-gray-500">December 3, 2025</span>
            </div>
          </div>
        </div>
      </div>
    </div>);

}